import { Dialog } from "@rio-cloud/rio-uikit";
import { useIntl } from "react-intl";
import { getStatusBadgeClass } from "../../utils/statusHelpers";
import { useBudgetVersionsDialog } from "../hooks/useBudgetVersionsDialog";
import { formatDateTimeWithTimezone } from "../../../../utils/dateUtils";
import { IBudget } from "../../../../models/budgetV2";

interface BudgetVersionsDialogProps {
  show: boolean;
  budget: IBudget;
  persona: string;

  onClose: () => void;
}

const SkeletonCell = ({ widthClassName = "w-24" }: { widthClassName?: string }) => (
  <div className={`h-[14px] ${widthClassName} animate-pulse rounded bg-gray-300`} />
);

const BudgetVersionsDialog = ({
  show,
  budget,
  persona,

  onClose,
}: BudgetVersionsDialogProps) => {
  const intl = useIntl();

  const {
    versions,
    currentVersion,
    loading,

    goToBudgetVersion,
  } = useBudgetVersionsDialog(budget.uuid, show, persona, onClose, budget.recipient);

  return (
    <>
      <Dialog
        show={show}
        title={intl.formatMessage({ id: "intl-msg:budgets.kanban.historyTitle" })}
        onClose={onClose}
        bsSize={Dialog.SIZE_LG}
        body={
          <div className="p-4">
            <div className="mb-6">
              <h3 className="text-size-16 font-semibold mb-4">
                {intl.formatMessage({ id: "intl-msg:budgets.kanban.historyTitle" })}
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="px-4 py-3 text-left text-size-12 font-semibold text-gray-700">
                        {intl.formatMessage({ id: "intl-msg:budgets.kanban.consultant" })}
                      </th>
                      <th className="px-4 py-3 text-left text-size-12 font-semibold text-gray-700">
                        {intl.formatMessage({ id: "intl-msg:budgets.kanban.recipient" })}
                      </th>
                      <th className="px-4 py-3 text-left text-size-12 font-semibold text-gray-700">
                        {intl.formatMessage({ id: "intl-msg:budgets.kanban.budget" })}
                      </th>
                      <th className="px-4 py-3 text-left text-size-12 font-semibold text-gray-700">
                        {intl.formatMessage({ id: "intl-msg:budgets.kanban.version" })}
                      </th>
                      <th className="px-4 py-3 text-left text-size-12 font-semibold text-gray-700">
                        {intl.formatMessage({ id: "intl-msg:budgets.kanban.date" })}
                      </th>
                      <th className="px-4 py-3 text-left text-size-12 font-semibold text-gray-700">
                        {intl.formatMessage({ id: "intl-msg:budgets.kanban.status" })}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr className="border-b border-gray-200">
                        <td className="px-4 py-3"><SkeletonCell widthClassName="w-28" /></td>
                        <td className="px-4 py-3"><SkeletonCell widthClassName="w-24" /></td>
                        <td className="px-4 py-3"><SkeletonCell widthClassName="w-32" /></td>
                        <td className="px-4 py-3"><SkeletonCell widthClassName="w-10" /></td>
                        <td className="px-4 py-3"><SkeletonCell widthClassName="w-36" /></td>
                        <td className="px-4 py-3"><SkeletonCell widthClassName="w-20" /></td>
                      </tr>
                    ) : currentVersion ? (
                      <tr className="border-b border-gray-200">
                        <td className="px-4 py-3 text-size-14 text-gray-800">
                          {budget.consultantName}
                        </td>
                        <td className="px-4 py-3 text-size-14 text-gray-800">
                          {currentVersion.recipientLabel}
                        </td>
                        <td className="px-4 py-3 text-size-14">
                          <button
                            onClick={() => goToBudgetVersion(currentVersion)}
                            className="text-blue-600 hover:text-blue-800 underline flex items-center gap-2"
                          >
                            <span className="rioglyph rioglyph-file"></span>
                            <span>{intl.formatMessage({ id: "intl-msg:budgets.kanban.viewBudget" })}</span>
                          </button>
                        </td>
                        <td className="px-4 py-3 text-size-14 text-gray-800">
                          v{currentVersion.versionNumber}
                        </td>
                        <td className="px-4 py-3 text-size-14 text-gray-800">
                          {formatDateTimeWithTimezone(currentVersion.createdAt || "")}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`px-3 py-1 rounded-full text-size-12 font-medium ${getStatusBadgeClass(currentVersion.status || "")}`}
                          >
                            {currentVersion.status}
                          </span>
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mt-6">
              <h3 className="text-size-16 font-semibold mb-4">
                {intl.formatMessage({ id: "intl-msg:budgets.kanban.historyChangesTitle" })}
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="px-4 py-3 text-left text-size-12 font-semibold text-gray-700">
                        {intl.formatMessage({ id: "intl-msg:budgets.kanban.profile" })}
                      </th>
                      <th className="px-4 py-3 text-left text-size-12 font-semibold text-gray-700">
                        {intl.formatMessage({ id: "intl-msg:budgets.kanban.viewBudget" })}
                      </th>
                      <th className="px-4 py-3 text-left text-size-12 font-semibold text-gray-700">
                        {intl.formatMessage({ id: "intl-msg:budgets.kanban.version" })}
                      </th>
                      <th className="px-4 py-3 text-left text-size-12 font-semibold text-gray-700">
                        {intl.formatMessage({ id: "intl-msg:budgets.kanban.versionDate" })}
                      </th>
                      <th className="px-4 py-3 text-left text-size-12 font-semibold text-gray-700">
                        {intl.formatMessage({ id: "intl-msg:budgets.kanban.status" })}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading
                      ? Array.from({ length: 3 }).map((_, index) => (
                          <tr key={`skeleton-version-${index}`} className="border-b border-gray-200">
                            <td className="px-4 py-3"><SkeletonCell widthClassName="w-24" /></td>
                            <td className="px-4 py-3"><SkeletonCell widthClassName="w-32" /></td>
                            <td className="px-4 py-3"><SkeletonCell widthClassName="w-10" /></td>
                            <td className="px-4 py-3"><SkeletonCell widthClassName="w-36" /></td>
                            <td className="px-4 py-3"><SkeletonCell widthClassName="w-20" /></td>
                          </tr>
                        ))
                      : versions.map((version, index) => (
                          <tr
                            key={`${version.versionNumber}-${index}`}
                            className="border-b border-gray-200"
                          >
                            <td className="px-4 py-3 text-size-14 text-gray-800">
                              {version.originLabel}
                            </td>
                            <td className="px-4 py-3 text-size-14">
                              <button
                                onClick={() => goToBudgetVersion(version)}
                                className="text-blue-600 hover:text-blue-800 underline flex items-center gap-2"
                              >
                                <span className="rioglyph rioglyph-file"></span>
                                <span>{intl.formatMessage({ id: "intl-msg:budgets.kanban.viewBudget" })}</span>
                              </button>
                            </td>
                            <td className="px-4 py-3 text-size-14 text-gray-800">
                              v{version.versionNumber}
                            </td>
                            <td className="px-4 py-3 text-size-14 text-gray-800">
                              {formatDateTimeWithTimezone(version.createdAt || "")}
                            </td>
                            <td className="px-4 py-3">
                              <span
                                className={`px-3 py-1 rounded-full text-size-12 font-medium ${getStatusBadgeClass(version.status || "")}`}
                              >
                                {version.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        }
      />
    </>
  );
};

export default BudgetVersionsDialog;
